import React from 'react';
import { t } from 'ttag';
import { Page, Text, View, Document, StyleSheet, Image, Font } from '@react-pdf/renderer';
import { has, compareByName } from '../../services/helper';
import getStepTitle from '../../services/helper/stepTitle';
import resolveTotalPrice from '../../services/helper/resolvePrice';
import Price from '../util/Price';

Font.register({
    family: 'Roboto',
    fonts: [
        {
            src: '/static/configurator/assets/fonts/Roboto/Roboto-Regular.ttf',
        },
        {
            src: '/static/configurator/assets/fonts/Roboto/Roboto-Black.ttf',
            fontWeight: 'bold',
        },
    ],
});

Font.register({
    family: 'BellMT',
    fonts: [
        { src: '/static/configurator/assets/fonts/BellMT/BellMT-Regular.ttf' },
    ],
});

// Create styles
const styles = StyleSheet.create({
    page: { padding: 20, paddingBottom: 50 },
    header: { textAlign: 'center', marginBottom: 12 },
    footer: { position: 'absolute', bottom: 12, left: 0, right: 0, textAlign: 'center' },
    logo: { marginHorizontal: 'auto', marginBottom: 12, width: 64 },
    headline: { fontFamily: 'BellMT', textTransform: 'uppercase', color: '#666666', fontSize: 10 },
    price: { flexDirection: 'row', justifyContent: 'space-between', marginTop: 4 },
    container: { marginTop: 4, flexDirection: 'row' },
    section: { marginVertical: 12 },
    title: { fontFamily: 'Roboto', fontSize: 10, fontWeight: 'bold' },
    text: { fontFamily: 'Roboto', fontSize: 10 },
    box: { border: 1, padding: 6, width: '100%' },
    checkbox: { border: 1, padding: 6, marginRight: 12 },
    emptyField: { borderTop: 1, paddingVertical: 12 },
    basicCol_1: { width: '70%', textAlign: 'left' },
    basicCol_2: { width: '15%', textAlign: 'right' },
    basicCol_3: { width: '15%', textAlign: 'right' },
    col_1: { width: '15%', textAlign: 'left' },
    col_2: { width: '55%', textAlign: 'left' },
    col_3: { width: '15%', textAlign: 'right' },
    col_4: { width: '15%', textAlign: 'right' },
    nlPriceHint: { marginVertical: 30 },
});

const resolvePrice = (part, configuration) => resolveTotalPrice(
    part, part.serial_parent_uid, configuration
);

function listConfiguration(configurations) {
    // Entfernt Duplikate anhand eines gegebenen Keys und arbeitet IMMER mit einem Plain-Array
    const uniqueBy = (arr, key) => {
        const seen = new Set();
        const plain = Array.from(arr || []); // verhindert sort() auf Immutable
        return plain.filter(obj => {
            const val = obj && obj[key];
            if (val == null || seen.has(val)) return false;
            seen.add(val);
            return true;
        });
    };

    let keys = Object.keys(configurations);
    keys = keys.filter(value => value !== 'engine');

    const listView = (
        keys.map((key) => (
            <View key={key} style={styles.section}>
                <Text style={styles.title}>{getStepTitle(key)}</Text>
                {
                    !Array.isArray(configurations[key]) ?
                        <View style={styles.container}>
                            <View style={styles.col_1}>
                                <Text style={styles.text}>{has(configurations[key], 'code') ? configurations[key].code : ''}</Text>
                            </View>
                            <View style={styles.col_2}>
                                <Text style={styles.text}>{has(configurations[key], 'name') ? configurations[key].name : ''}</Text>
                            </View>
                            <View style={styles.col_3}>
                                <Text style={styles.text}>{has(configurations[key], 'weight') ? t`${configurations[key].weight} kg` : t`0 kg`}</Text>
                            </View>
                            <View style={styles.col_4}>
                                <Text style={styles.text}>
                                    <Price value={has(configurations[key], 'total_price')
                                        ? resolvePrice(configurations[key], configurations)
                                        : 0}
                                    />
                                </Text>
                            </View>
                        </View>
                        :
                        uniqueBy(configurations[key], 'uid').sort(compareByName).map((item) => (
                            <View key={item.uid} style={styles.container}>
                                <View style={styles.col_1}>
                                    <Text style={styles.text}>{has(item, 'code') ? item.code : ''}</Text>
                                </View>
                                <View style={styles.col_2}>
                                    <Text style={styles.text}>{has(item, 'name') ? item.name : ''}</Text>
                                </View>
                                <View style={styles.col_3}>
                                    <Text style={styles.text}>{has(item, 'weight') ? t`${item.weight} kg` : t`0 kg`}</Text>
                                </View>
                                <View style={styles.col_4}>
                                    <Text style={styles.text}>
                                        <Price value={has(item, 'total_price')
                                            ? resolvePrice(item, configurations)
                                            : 0}
                                        />
                                    </Text>
                                </View>
                            </View>
                        ))
                }
            </View>
        ))
    );
    return listView;
}

// Create Customer PDF
const CustomerPdf = (props) => {
    const language = props.model.language;
    const barzahlung = t`Barzahlungspreis`;
    const taxRate = t`tax-rate`;
    const netPrice = props.price / (1 + (parseFloat(taxRate / 100)));

    return (
        <Document>
            <Page style={styles.page} wrap>
                <View style={styles.header} fixed>
                    <Image style={styles.logo} src="/static/configurator/assets/images/la_strada_logo.png" />
                    <Text style={styles.headline}>{t`Die Deutsche Manufaktur`}</Text>
                    <Text style={styles.headline}>{t`für Reisemobile`}</Text>
                </View>
                <View>
                    <View style={styles.section}>
                        <Text style={styles.title}>{has(props.model, 'name') ? props.model.name : ''}</Text>
                        <View style={styles.container}>
                            <View style={styles.basicCol_1}>
                                <Text style={styles.title}>
                                    {has(props.configurations.engine, 'name') ? props.configurations.engine.name : ''}
                                </Text>
                            </View>
                            <View style={styles.basicCol_2}>
                                <Text style={styles.title}>
                                    {has(props.configurations.engine, 'weight') ? props.configurations.engine.weight : ''} kg
                                </Text>
                            </View>
                            <View style={styles.basicCol_3}>
                                <Text style={styles.title}>
                                    <Price value={has(props.configurations.engine, 'total_price')
                                        ? resolvePrice(props.configurations.engine,
                                            props.configurations)
                                        : 0}
                                    />
                                </Text>
                            </View>
                        </View>
                    </View>
                    <View style={styles.container}>
                        <View style={styles.col_1}>
                            <Text style={styles.title}>{t`Code`}</Text>
                        </View>
                        <View style={styles.col_2}>
                            <Text style={styles.title}>{t`Bezeichnung`}</Text>
                        </View>
                        <View style={styles.col_3}>
                            <Text style={styles.title}>{t`Gewicht`}</Text>
                        </View>
                        <View style={styles.col_4}>
                            <Text style={styles.title}>{t`Preis`}</Text>
                        </View>
                    </View>
                    {listConfiguration(props.configurations)}
                </View>
                <View style={styles.section}>
                    {/* eslint-disable-next-line func-names,consistent-return */}
                    {(function() {
                        if (language !== 7) {
                            return (<View style={styles.price}>
                                <Text style={styles.text}>{t`Netto Preis`}</Text>
                                <Text style={styles.text}>
                                    <Price value={netPrice} />
                                </Text>
                            </View>);
                        }
                    }())}
                    {/* eslint-disable-next-line func-names,consistent-return */}
                    {(function() {
                        if (language !== 7) {
                            return (<View style={styles.price}>
                                <Text style={styles.text}>+ {t`Mehrwertsteuer 19%`}</Text>
                                <Text style={styles.text}>
                                    <Price value={props.price - netPrice} />
                                </Text>
                            </View>);
                        }
                    }())}
                    <View style={styles.price}>
                        <Text style={styles.text}>{barzahlung}</Text>
                        <Text style={styles.title}>
                            <Price value={props.price} />
                        </Text>
                    </View>
                    <View style={styles.price}>
                        <Text style={styles.text}>{t`Gewicht Fahrzeug`}</Text>
                        <Text style={styles.text}>{Math.round(props.weight)} kg</Text>
                    </View>
                    <View style={styles.nlPriceHint}>
                        <Text style={styles.text}>{t`NlPreisHinweis`}</Text>
                    </View>
                </View>

                <View style={styles.footer} fixed>
                    <Text
                        style={[styles.headline, { fontSize: 8, marginVertical: 12 }]}
                    >
                        {t`www.lastrada-mobile.de`}
                    </Text>
                    <Text
                        style={styles.text}
                        render={({ pageNumber, totalPages }) => (
                            t`Seite ${pageNumber} / ${totalPages}`
                        )}
                        fixed
                    />
                </View>
            </Page>
        </Document >
    );
};

const SellerPdf = (props) => {
    const language = props.model.language;
    let barzahlung = t`Barzahlungspreis`;
    if (language === 7) {
        barzahlung += ' (Endkundenpreis inkl. aller Steuern)';
    }
    const formData = props.formData;
    const taxRate = t`tax-rate`;
    const netPrice = props.price / (1 + (parseFloat(taxRate / 100)));

    return (
        <Document>
            <Page style={styles.page} wrap>
                <View style={styles.header} fixed>
                    <Image style={styles.logo} src="/static/configurator/assets/images/la_strada_logo.png" />
                    <Text style={styles.headline}>{t`Die Deutsche Manufaktur`}</Text>
                    <Text style={styles.headline}>{t`für Reisemobile`}</Text>
                </View>
                <View style={styles.section}>
                    <View style={styles.container}>
                        <View style={styles.checkbox} />
                        <Text style={styles.text}>{t`ANGEBOT für ein neues Wohnmobil`}</Text>
                    </View>
                    <View style={styles.container}>
                        <View style={styles.checkbox} />
                        <Text style={styles.text}>
                            {t`VERBINDLICHE BESTELLUNG für ein neues Wohnmobil`}
                        </Text>
                    </View>
                </View>
                <View style={[styles.section, { flexDirection: 'row', justifyContent: 'space-between' }]}>
                    <View style={[styles.box, { width: '38%' }]}>
                        <Text style={[styles.title]}>{t`Verkäufer`}:</Text>
                        <Text style={[styles.text, { marginVertical: 6 }]}>
                            {props.userData.company}
                        </Text>
                        <Text style={styles.text}>{props.userData.name}</Text>
                        <Text style={styles.text}>{props.userData.address}</Text>
                        <Text style={styles.text}>{props.userData.zip} {props.userData.city}</Text>
                        <Text style={styles.text}>{props.userData.country}</Text>
                        <Text style={styles.text}>
                            {has(props.userData, 'telephone') ? props.userData.telephone : ''}
                        </Text>
                        <Text style={styles.text}>
                            {has(props.userData, 'fax') ? props.userData.fax : ''}
                        </Text>
                        <Text style={styles.text}>
                            {has(props.userData, 'email') ? props.userData.email : ''}
                        </Text>
                    </View>
                    <View style={[styles.box, { width: '60%', flexDirection: 'row' }]}>
                        <View style={{ flexDirection: 'column', justifyContent: 'space-between' }}>
                            <Text style={styles.title}>{t`Käufer`}:</Text>
                            <Text style={styles.text}>
                                {(formData.salutation === 'salutation-1' || formData.salutation === 'Herr') ? t`Herr` : t`Frau`} {formData.first_name} {formData.last_name}
                            </Text>
                            <Text style={styles.text}>
                                {formData.street}
                            </Text>
                            <Text style={styles.text}>
                                {formData.zip} {formData.city}
                            </Text>
                            <Text style={styles.text}>
                                {formData.country}
                            </Text>
                            <Text style={styles.text}>
                                &nbsp;
                            </Text>
                            <Text style={styles.text}>
                                {t`E-Mail`}: {formData.email}
                            </Text>
                            <Text style={styles.text}>
                                {t`Tel.`}: {formData.phone}
                            </Text>
                            <Text style={styles.text}>
                                {t`Geb. am`}: {formData.date_of_birth}
                            </Text>
                            <Text style={styles.text}>
                                {t`Geb. in`}: {formData.place_of_birth}
                            </Text>
                            <Text style={styles.text}>
                                {t`Beruf/Gewerbe`}: {formData.industry}
                            </Text>
                            <Text style={styles.text}>
                                {t`Ausweis-Nr./Ort`}: {formData.id_number_place}
                            </Text>
                        </View>
                    </View>
                </View>
                <View style={styles.section}>
                    <Text style={styles.text}>
                        {t`Der Käufer bestellt auf Grund der nachstehenden und anbei befindlichen Verkaufsbedingungen, von denen Kenntnis genommen wurde, das nachstehend beschriebene Fahrzeug in Serien-/Sonderausführung.`}
                    </Text>
                </View>
                <View style={styles.section}>
                    <View style={[styles.box, { width: '100%', flexDirection: 'row' }]}>
                        <View style={{ flexDirection: 'row' }}>
                            <View style={{ width: '33%' }}>
                                <Text style={styles.text}>{t`Fabrikat`}: </Text>
                            </View>
                            <View style={{ width: '33%' }}>
                                <Text style={styles.text}>{t`Typ`}: </Text>
                            </View>
                            <View style={{ width: '34%' }}>
                                <Text style={styles.text}>{t`Fahrzeugart`}: </Text>
                            </View>
                        </View>
                    </View>
                    <View style={[styles.box, { borderTop: 0, width: '100%', flexDirection: 'row' }]}>
                        <View style={{ flexDirection: 'row' }}>
                            <View style={{ width: '33%' }}>
                                <Text style={styles.text}>{t`Fahrwerk`}: </Text>
                            </View>
                            <View style={{ width: '33%' }}>
                                <Text style={styles.text}>{t`Lackierung`}: </Text>
                            </View>
                            <View style={{ width: '34%' }}>
                                <Text style={styles.text}>{t`Polsterung`}: </Text>
                            </View>
                        </View>
                    </View>
                    <View style={[styles.box, { borderTop: 0, width: '100%', flexDirection: 'row' }]}>
                        <View style={{ flexDirection: 'row' }}>
                            <View style={{ width: '33%' }}>
                                <Text style={styles.text}>{t`Motor`}: </Text>
                            </View>
                            <View style={{ width: '33%' }}>
                                <Text style={styles.text}>{t`Bereifung`}: </Text>
                            </View>
                            <View style={{ width: '34%' }}>
                                <Text style={styles.text}>{t`Fahrgestellnummer`}: </Text>
                            </View>
                        </View>
                    </View>
                    <View style={[styles.box, { borderTop: 0, width: '100%', flexDirection: 'row' }]}>
                        <View style={{ flexDirection: 'row' }}>
                            <View style={{ width: '100%' }}>
                                <Text style={styles.text}>{t`Unverbindlicher Liefertermin/Lieferzeit`}: </Text>
                            </View>
                        </View>
                    </View>
                </View>
                <View style={styles.section}>
                    <Text style={styles.title}>{has(props.model, 'name') ? props.model.name : ''}</Text>
                    <View style={styles.container}>
                        <View style={styles.basicCol_1}>
                            <Text style={styles.title}>
                                {has(props.configurations.engine, 'name') ? props.configurations.engine.name : ''}
                            </Text>
                        </View>
                        <View style={styles.basicCol_2}>
                            <Text style={styles.title}>
                                {has(props.configurations.engine, 'weight') ? props.configurations.engine.weight : ''} kg
                            </Text>
                        </View>
                        <View style={styles.basicCol_3}>
                            <Text style={styles.title}>
                                <Price value={has(props.configurations.engine, 'total_price')
                                    ? resolvePrice(
                                        props.configurations.engine,
                                        props.configurations)
                                    : 0}
                                />
                            </Text>
                        </View>
                    </View>
                </View>
                <View style={styles.container}>
                    <View style={styles.col_1}>
                        <Text style={styles.title}>{t`Code`}</Text>
                    </View>
                    <View style={styles.col_2}>
                        <Text style={styles.title}>{t`Bezeichnung`}</Text>
                    </View>
                    <View style={styles.col_3}>
                        <Text style={styles.title}>{t`Gewicht`}</Text>
                    </View>
                    <View style={styles.col_4}>
                        <Text style={styles.title}>{t`Preis`}</Text>
                    </View>
                </View>
                {listConfiguration(props.configurations)}
                <View style={styles.section}>
                    {/* eslint-disable-next-line func-names,consistent-return */}
                    {(function() {
                        if (language !== 7) {
                            return (<View style={styles.price}>
                                <Text style={styles.text}>{t`Netto Preis`}</Text>
                                <Text style={styles.text}>
                                    <Price value={netPrice} />
                                </Text>
                            </View>);
                        }
                    }())}
                    {/* eslint-disable-next-line func-names,consistent-return */}
                    {(function() {
                        if (language !== 7) {
                            return (<View style={styles.price}>
                                <Text style={styles.text}>+ {t`Mehrwertsteuer 19%`}</Text>
                                <Text style={styles.text}>
                                    <Price value={props.price - netPrice} />
                                </Text>
                            </View>);
                        }
                    }())}
                    <View style={styles.price}>
                        <Text style={styles.text}>{barzahlung}</Text>
                        <Text style={styles.title}>
                            <Price value={props.price} />
                        </Text>
                    </View>
                    <View style={styles.price}>
                        <Text style={styles.text}>{t`Gewicht Fahrzeug`}</Text>
                        <Text style={styles.text}>{Math.round(props.weight)} kg</Text>
                    </View>
                    <View style={styles.nlPriceHint}>
                        <Text style={styles.text}>{t`NlPreisHinweis`}</Text>
                    </View>
                </View>
                <View style={styles.section}>
                    <View style={styles.box}>
                        <View style={[styles.container, { flexDirection: 'column', paddingBottom: 24 }]}>
                            <Text style={styles.title}>
                                {t`Zahlungsweise und sonstige Vereinbarungen`}
                            </Text>
                            <Text style={styles.text}>
                                {t`(mit Vorrang gegenüber anbeiliegenden Verkaufsbedingungen)`}
                            </Text>
                        </View>
                        <View style={styles.emptyField} />
                        <View style={styles.emptyField} />
                        <View style={styles.emptyField} />
                    </View>
                </View>
                <View style={styles.section}>
                    <Text style={styles.text}>
                        {t`Bis zur Tilgung des gesamten Kaufpreises bleibt das Fahrzeug Eigentum des Verkäufers.`}
                    </Text>
                    <Text style={styles.title}>
                        {t`Die Verkaufsangestellten sind nur bei schriftlicher Ermächtigung zur Annahme von Zahlungen befugt!`}
                    </Text>
                    <Text style={styles.text}>
                        {t`An diese Bestellung, von der ich (wir) ein Exemplar erhalten habe(n), bin ich (sind wir) höchstens bis drei Wochen, bei Nutzfahrzeugen bis sechs Wochen gebunden. Diese Frist verkürzt sich auf 10 Tage (bei Nutzfahrzeugen auf 2 Wochen) bei Fahrzeugen, die beim Verkäufer vorhanden sind. Der Kaufvertrag ist abgeschlossen, wenn der Verkäufer die Annahme der Bestellung des näher bezeichneten Kaufgegenstandes innerhalb der jeweils genannten Fristen schriftlich bestätigt oder die Lieferung ausführt.`}
                    </Text>
                </View>
                <View style={styles.section}>
                    <View style={styles.box}>
                        <Text style={[styles.text, { marginBottom: 24 }]}>
                            {t`Ein Exemplar dieser Bestellung inkl. AGB habe ich erhalten`}:
                        </Text>
                        <View style={styles.container}>
                            <Text style={styles.text}>{t`Stadt, Datum`}</Text>
                            <Text style={[styles.text, { marginHorizontal: 'auto' }]}>{t`Unterschrift des Käufers`}</Text>
                        </View>
                    </View>
                </View>
                <View style={styles.footer} fixed>
                    <Text
                        style={[styles.headline, { fontSize: 8, marginVertical: 12 }]}
                    >
                        www.lastrada-mobile.de
                    </Text>
                    <Text
                        style={styles.text}
                        render={({ pageNumber, totalPages }) => (
                            t`Seite ${pageNumber} / ${totalPages}`
                        )}
                        fixed
                    />
                </View>
            </Page>
        </Document>
    );
};

export { CustomerPdf, SellerPdf };
